// Fixed-layout labels (drawn in mm, never stretched or squashed; text is made smaller step by step when it doesn't fit):
// the polybag sticker, the printed carton marking table and the numbered carton label. Every text can print in the
// organisation's other label language (Settings → Languages & translations), right to left where the script needs it.
import { CODE128_RULES, EAN13, code128Widths, ean13Modules } from "./barcode.js";
import { isRtlText, missingText } from "./languages.js";
import { BATCH_PLACEHOLDER, escX, packagingLogo, svgDoc, type LabelData, type LabelDesign, type LabelI18n, type RenderResult } from "./labels.js";

/** Width of a line of text in mm (Helvetica-like proportions), for fitting — letters keep their normal proportions. */
export function textWidthMm(s: string, sizeMm: number, bold = false): number {
  let em = 0;
  for (const ch of s) {
    em += ch === " " ? 0.28 : /[il.,:;|!'’\-()[\]]/.test(ch) ? 0.27 : /[mwMW@%]/.test(ch) ? 0.86 : /[A-Z]/.test(ch) ? 0.69 : /[0-9]/.test(ch) ? 0.56
      : /[؀-ۿݐ-ݿﭐ-﻿]/.test(ch) ? 0.46 : /[֐-׿]/.test(ch) ? 0.55 : 0.53;
  }
  return em * sizeMm * (bold ? 1.07 : 1);
}
/** One line of SVG text; right-to-left text gets direction="rtl" (the browser joins and orders the letters). */
function t(x: number, y: number, s: string, size: number, o: { bold?: boolean; anchor?: "start" | "middle" | "end"; fill?: string } = {}): string {
  const rtl = isRtlText(s);
  // In right-to-left text "start" is the right end: keep what the caller means on the page.
  const anchor = rtl ? (o.anchor === "start" || !o.anchor ? "end" : o.anchor === "end" ? "start" : "middle") : o.anchor || "start";
  return `<text x="${x.toFixed(2)}" y="${y.toFixed(2)}" font-size="${size.toFixed(3)}" font-weight="${o.bold ? 700 : 400}" text-anchor="${anchor}"${rtl ? ' direction="rtl" data-rtl="1"' : ""}${o.fill ? ` fill="${o.fill}"` : ""}>${escX(s)}</text>`;
}
/** Break text into at most `max` lines of `width` mm at a word boundary; the last line ends with "…" if it still doesn't fit. */
export function fitLines(s: string, width: number, size: number, bold: boolean, max: number): { lines: string[]; cut: boolean } {
  const words = String(s || "").split(/\s+/).filter(Boolean);
  const out: string[] = [];
  let cur = "";
  for (const w of words) {
    const next = cur ? `${cur} ${w}` : w;
    if (textWidthMm(next, size, bold) <= width || !cur) cur = next;
    else { out.push(cur); cur = w; }
  }
  if (cur) out.push(cur);
  if (out.length <= max && out.every((l) => textWidthMm(l, size, bold) <= width)) return { lines: out, cut: false };
  const keep = out.slice(0, max);
  let last = keep[max - 1] ?? "";
  if (out.length > max || textWidthMm(last, size, bold) > width) {
    while (last && textWidthMm(`${last}…`, size, bold) > width) last = last.slice(0, -1).trimEnd();
    keep[max - 1] = `${last}…`;
  }
  return { lines: keep, cut: true };
}
/** The second language chosen for a design, if the design prints one. */
export function secondLanguage(d: LabelDesign, data: LabelData): LabelI18n | undefined {
  if (d.langMode === "main" || !data.i18n) return undefined;
  return data.i18n[d.secondLang] || Object.values(data.i18n)[0];
}
/** A translated value (or "" and a warning when it's missing — the main text then prints, never blank). */
function tr(L: LabelI18n | undefined, field: keyof LabelI18n, main: string, warnings: string[]): string {
  if (!L || !main) return "";
  const v = L[field] as string;
  if (v) return v;
  warnings.push(missingText(L.name, L.missing[field as string] || main));
  return "";
}
const caption = (L: LabelI18n | undefined, c: string, warnings: string[]) => {
  if (!L) return "";
  const v = L.captions[c];
  if (!v) warnings.push(missingText(L.name, `the caption "${c}"`));
  return v || "";
};

// ---- Barcodes -------------------------------------------------------------------------------------------------------------
/** A barcode centred on cx: EAN-13 when the size has one, else the SKU as Code 128. Returns the drawing and its width. */
function barcode(data: LabelData, cx: number, y: number, maxW: number, h: number, warnings: string[], quietWarn = true): { svg: string; w: number; digits: string } {
  if (data.ean && /^\d{13}$/.test(data.ean)) {
    const mag = Math.min(1, maxW / EAN13.widthMm), X = (EAN13.widthMm * mag) / 113, mods = ean13Modules(data.ean);
    if (mag < EAN13.minMagnification) warnings.push(`The EAN-13 barcode would be ${Math.round(mag * 100)}% of its standard size: below the 80% minimum for reliable scanning. Make the label wider.`);
    // A shortened ("truncated") EAN-13 should keep at least half the full bar height (22.85 mm at 100%).
    const recommended = EAN13.barHeightMm * mag * 0.5;
    if (quietWarn && h < recommended - 0.05) warnings.push(`The EAN-13 bars are ${h.toFixed(1)} mm tall: shorter than the ${recommended.toFixed(1)} mm GS1 recommends as the lowest height for a shortened EAN-13 at this size. Make the barcode taller.`);
    const bx = cx - (95 * X) / 2;
    let bars = "";
    for (let i = 0; i < 95; i++) if (mods[i] === "1") bars += `<rect x="${(bx + i * X).toFixed(3)}" y="${y.toFixed(2)}" width="${X.toFixed(3)}" height="${h.toFixed(2)}"/>`;
    return { svg: `<g fill="#000">${bars}</g>`, w: 95 * X, digits: data.ean.replace(/^(\d)(\d{6})(\d{6})$/, "$1 $2 $3") };
  }
  const code = data.sku || data.productCode;
  if (!code) return { svg: t(cx, y + h / 2, "[Barcode — no SKU or EAN yet]", 2.5, { anchor: "middle", fill: "#888" }), w: 0, digits: "" };
  const widths = code128Widths(code), modules = widths.reduce((a, b) => a + b, 0) + CODE128_RULES.quietModules * 2;
  const X = Math.min(0.33, maxW / modules);
  if (X < CODE128_RULES.minModuleMm) warnings.push(`The Code 128 barcode's bars would be ${X.toFixed(2)} mm: below ${CODE128_RULES.minModuleMm} mm, too thin to print and scan reliably. Make the label wider or add an EAN.`);
  const total = (modules - CODE128_RULES.quietModules * 2) * X;
  let bx = cx - total / 2, bars = "", bar = true;
  for (const n of widths) { if (bar) bars += `<rect x="${bx.toFixed(3)}" y="${y.toFixed(2)}" width="${(n * X).toFixed(3)}" height="${h.toFixed(2)}"/>`; bx += n * X; bar = !bar; }
  return { svg: `<g fill="#000">${bars}</g>`, w: total, digits: "" };
}

// ---- C. Polybag sticker ---------------------------------------------------------------------------------------------------
/** Sizes offered for the polybag sticker (mm). */
export const STICKER_SIZES: Array<{ name: string; widthMm: number; heightMm: number }> = [
  { name: "Standard 75 × 50", widthMm: 75, heightMm: 50 }, { name: "Small 60 × 40", widthMm: 60, heightMm: 40 }, { name: "Wide 100 × 50", widthMm: 100, heightMm: 50 },
];
/**
 * The polybag sticker, a fixed layout at 75 × 50 mm scaled in proportion for other sizes: 3 mm margins; the garment
 * description bold (up to 2 lines, "…" if longer — the space for 2 lines is always kept so nothing else moves); "Size:"
 * with the size bold and largest; "Colour:" with the colour bold; the barcode centred (default 12 mm high) with the SKU
 * on one line centred under it (made smaller down to 2.5 mm, never stretched); the batch number bottom left.
 */
export function renderSticker(d: LabelDesign, data: LabelData): RenderResult {
  const W = d.widthMm, H = d.heightMm, k = Math.min(W / 75, H / 50);
  const warnings: string[] = [];
  const m = 3 * k, cw = W - 2 * m;
  const shown = (key: string) => { const f = d.fields.find((x) => x.key === key); return f ? f.show : true; };
  const L = secondLanguage(d, data);
  const parts: string[] = [`<rect x="0.1" y="0.1" width="${(W - 0.2).toFixed(2)}" height="${(H - 0.2).toFixed(2)}" rx="0.8" fill="#fff" stroke="#9aa4b2" stroke-width="0.2"/>`];
  // 1–2. Description, bold, left: one line if it fits, else two (at a word boundary), else "…" and a warning.
  const descSize = 3.8 * k, descLine = descSize * 1.18;
  let y = m;
  if (shown("description")) {
    const main = data.description || "";
    const sec = tr(L, "description", main, warnings);
    let ls: string[];
    if (L && d.langMode === "second") {
      const f = fitLines(sec || main, cw, descSize, true, 2);
      ls = f.lines;
      if (f.cut) warnings.push("The description is too long for the sticker: it is shortened with “…”. Make the sticker wider or the product name shorter.");
    } else if (L && sec) {
      // Bilingual: one line each.
      const a = fitLines(main, cw, descSize, true, 1), b = fitLines(sec, cw, descSize, true, 1);
      ls = [a.lines[0] || "", b.lines[0] || ""];
      if (a.cut || b.cut) warnings.push("The description is too long for one line in each language: it is shortened with “…”.");
    } else {
      const f = fitLines(main, cw, descSize, true, 2);
      ls = f.lines;
      if (f.cut) warnings.push("The description is too long for the sticker: it is shortened with “…”. Make the sticker wider or the product name shorter.");
    }
    ls.forEach((l, i) => { if (l) parts.push(t(isRtlText(l) ? W - m : m, y + descSize * 0.85 + i * descLine, l, descSize, { bold: true, anchor: isRtlText(l) ? "end" : "start" })); });
  }
  y += descLine * 2 + 1.2 * k;   // always room for two lines, so the rest stays in place
  // Size: regular caption, value bold and the largest text.
  const capSize = 3.8 * k, sizeSize = 5 * k;
  const sizeCap = L && d.langMode === "second" ? caption(L, "Size", warnings) || "Size" : "Size";
  if (shown("size")) {
    const base = y + sizeSize * 0.85;
    const label = `${sizeCap}:`;
    parts.push(t(m, base, label, capSize));
    parts.push(t(m + textWidthMm(label, capSize) + 1.2 * k, base, data.size || "", sizeSize, { bold: true }));
  }
  y += sizeSize * 1.15;
  if (shown("colour")) {
    const base = y + capSize * 0.85;
    const colCap = L && d.langMode === "second" ? caption(L, "Colour", warnings) || "Colour" : "Colour";
    const sec = tr(L, "colour", data.colour, warnings);
    const value = L && d.langMode === "second" ? sec || data.colour : L && sec ? `${data.colour} / ${sec}` : data.colour;
    const label = `${colCap}:`;
    parts.push(t(m, base, label, capSize));
    parts.push(t(m + textWidthMm(label, capSize) + 1.2 * k, base, value || "", capSize, { bold: true }));
  }
  y += capSize * 1.3 + 1.2 * k;
  // Barcode centred on the label; the SKU on one line, centred on the barcode's centre line.
  const bh = (d.pack?.barcodeMm && d.pack.barcodeMm > 0 ? d.pack.barcodeMm : 12) * k;
  if (shown("barcode")) {
    const b = barcode(data, W / 2, y, cw, bh, warnings);
    parts.push(b.svg);
    y += bh;
    if (b.digits) { y += 2.2 * k; parts.push(t(W / 2, y, b.digits, 2.2 * k, { anchor: "middle" })); }
  }
  if (shown("sku") && data.sku) {
    let size = 3.5 * k;
    while (textWidthMm(data.sku, size) > cw && size > 2.5 * k + 0.001) size = Math.max(2.5 * k, size - 0.1 * k);
    if (textWidthMm(data.sku, size) > cw) warnings.push(`The SKU ${data.sku} doesn't fit on one line even at 2.5 mm: make the sticker wider.`);
    y += size * 1.1;
    parts.push(t(W / 2, y, data.sku, size, { anchor: "middle" }));
  }
  // Batch number, bottom left.
  if (shown("batch")) {
    const bs = 3 * k, cap = L && d.langMode === "second" ? caption(L, "Batch", warnings) || "Batch" : "Batch";
    parts.push(t(m, H - m, `${cap}: ${data.batch || BATCH_PLACEHOLDER}`, bs));
    if (y > H - m - bs - 0.3) warnings.push(`The sticker content needs more room than the ${W} × ${H} mm sticker has: make it larger or the barcode lower.`);
  }
  return { svg: svgDoc(W, H, parts.join("")), warnings: [...new Set(warnings)], scale: k, usedMm: H };
}

// ---- E. Carton marking table (printed on the carton) and the carton label ---------------------------------------------------------
interface Row { caption: string; main: string; second?: string; span?: boolean; bold?: boolean; big?: boolean }
/** Rows of the marking table / carton label, in each language (second-language cells only when the design prints it). */
function markRows(d: LabelDesign, data: LabelData, warnings: string[], label: boolean): { rows: Row[]; L?: LabelI18n } {
  const L = secondLanguage(d, data), p = d.pack || {};
  const two = (field: keyof LabelI18n, main: string) => (L ? tr(L, field, main, warnings) : "");
  const sizeQty = [data.sizeBreakdown || (p.packingMethod === "ratio" ? "[Ratio — from packing list]" : data.size || "[Size — from packing list]"), data.quantity || (p.piecesPerCarton ? `${p.piecesPerCarton} pcs` : "[Qty]")].join(" · ");
  const weights = `${p.grossKg ? `${p.grossKg} kg` : "[kg]"} / ${p.netKg ? `${p.netKg} kg` : "[kg]"}`;
  const country = (data.madeIn || "").replace(/^Made in\s+/i, "") || data.country || "[Country]";
  const countrySecond = L ? L.country || "" : "";
  if (L && !L.country && country && !country.startsWith("[")) warnings.push(missingText(L.name, country));
  const rows: Row[] = [
    { caption: "PRODUCT", main: data.description, second: two("description", data.description) },
    { caption: "COLOUR", main: data.colour, second: two("colour", data.colour) },
    { caption: "COMPOSITION", main: data.composition, second: two("composition", data.composition) },
    { caption: "STYLE", main: data.productCode, span: true },
    ...(label ? [{ caption: "ORDER", main: [data.po ? `PO ${data.po}` : "[PO]", `Batch ${data.batch || BATCH_PLACEHOLDER}`].join(" · "), span: true }] : []),
    { caption: "SIZE · QTY", main: sizeQty, span: true },
    { caption: "G.W. / N.W.", main: weights, span: true },
    { caption: "COUNTRY OF ORIGIN", main: country, second: countrySecond },
    { caption: "CARTON NO", main: label ? (data.cartonNo ? data.cartonNo.replace(/\s+of\s+/i, " OF ") : "[x] OF [y]") : "____ OF ____", span: true, bold: label, big: label },
  ];
  return { rows, L };
}
/** A table drawn in mm: caption | main | second language (or caption | value spanning), ruled in the ink colour. */
function drawTable(x: number, y: number, w: number, rows: Row[], L: LabelI18n | undefined, d: LabelDesign, capSize: number, valSize: number, ink: string, warnings: string[]): { svg: string; h: number } {
  const bilingual = !!L && d.langMode === "both", secondOnly = !!L && d.langMode === "second";
  const pad = Math.max(0.8, valSize * 0.3);
  const capW = Math.min(w * 0.32, Math.max(...rows.map((r) => textWidthMm(r.caption, capSize, true))) + pad * 2);
  const valW = w - capW, halfW = bilingual ? valW / 2 : valW;
  const out: string[] = [];
  let cy = y;
  for (const r of rows) {
    const vs = r.big ? valSize * 1.25 : valSize;
    const main = secondOnly && r.second ? r.second : r.main;
    const cells: Array<{ x: number; w: number; text: string }> = bilingual && !r.span ? [{ x: x + capW, w: halfW, text: r.main }, { x: x + capW + halfW, w: halfW, text: r.second || "" }] : [{ x: x + capW, w: valW, text: main }];
    const wrapped = cells.map((c) => fitLines(c.text || "", c.w - pad * 2, vs, !!r.bold, 3));
    if (wrapped.some((f) => f.cut)) warnings.push(`"${r.caption}" is too long for its cell: shortened. Make the text smaller or the carton / label larger.`);
    const secondCap = bilingual || secondOnly ? caption(L, r.caption, warnings) : "";
    const capLines = secondCap ? 2 : 1;
    const rh = Math.max(capLines * capSize * 1.25, ...wrapped.map((f) => f.lines.length * vs * 1.2)) + pad * 2;
    out.push(`<rect x="${x.toFixed(2)}" y="${cy.toFixed(2)}" width="${w.toFixed(2)}" height="${rh.toFixed(2)}" fill="none" stroke="${ink}" stroke-width="${Math.max(0.25, valSize * 0.06).toFixed(2)}"/>`);
    out.push(`<line x1="${(x + capW).toFixed(2)}" y1="${cy.toFixed(2)}" x2="${(x + capW).toFixed(2)}" y2="${(cy + rh).toFixed(2)}" stroke="${ink}" stroke-width="${Math.max(0.25, valSize * 0.06).toFixed(2)}"/>`);
    if (cells.length > 1) out.push(`<line x1="${(x + capW + halfW).toFixed(2)}" y1="${cy.toFixed(2)}" x2="${(x + capW + halfW).toFixed(2)}" y2="${(cy + rh).toFixed(2)}" stroke="${ink}" stroke-width="${Math.max(0.2, valSize * 0.04).toFixed(2)}"/>`);
    out.push(t(x + pad, cy + pad + capSize * 0.9, r.caption, capSize, { bold: true, fill: ink }));
    if (secondCap) out.push(t(x + capW - pad, cy + pad + capSize * 2.1, secondCap, capSize, { fill: ink, anchor: "end" }));
    cells.forEach((c, i) => wrapped[i].lines.forEach((l, j) => {
      const rtl = isRtlText(l);
      // Right-to-left text sits against the cell's right edge; the rest against its left edge.
      out.push(t(rtl ? c.x + c.w - pad : c.x + pad, cy + pad + vs * 0.9 + j * vs * 1.2, l, vs, { bold: !!r.bold, fill: ink, anchor: rtl ? "end" : "start" }));
    }));
    cy += rh;
  }
  return { svg: out.join(""), h: cy - y };
}
/** Header: the brand logo on the left and the company name (in each language printed) on the right. */
function tableHeader(x: number, y: number, w: number, h: number, d: LabelDesign, data: LabelData, L: LabelI18n | undefined, mono: boolean, size: number, ink: string, warnings: string[]): string {
  const logo = packagingLogo(d, data, mono);
  if (logo.note) warnings.push(logo.note);
  const lw = w * 0.38;
  const out = [`<rect x="${x.toFixed(2)}" y="${y.toFixed(2)}" width="${w.toFixed(2)}" height="${h.toFixed(2)}" fill="none" stroke="${ink}" stroke-width="${Math.max(0.25, size * 0.06).toFixed(2)}"/>`];
  out.push(logo.url ? `<image href="${escX(logo.url)}" x="${(x + h * 0.12).toFixed(2)}" y="${(y + h * 0.12).toFixed(2)}" width="${(lw - h * 0.24).toFixed(2)}" height="${(h * 0.76).toFixed(2)}" preserveAspectRatio="xMinYMid meet"/>`
    : t(x + h * 0.15, y + h / 2 + size * 0.35, logo.name || "Logo", size * 0.8, { fill: ink }));
  const names = [data.companyName, L && d.langMode !== "main" ? tr(L, "companyName", data.companyName, warnings) : ""].filter(Boolean);
  const showNames = L && d.langMode === "second" ? names.slice(-1) : names;
  const lineH = size * 1.25, top = y + h / 2 - (showNames.length * lineH) / 2 + size * 0.9;
  showNames.forEach((n, i) => {
    const f = fitLines(n, w - lw - size, size, true, 1);
    out.push(t(x + w - size * 0.5, top + i * lineH, f.lines[0] || "", size, { bold: true, fill: ink, anchor: "end" }));
  });
  return out.join("");
}

/** Marking text size: 8 mm values and 5 mm captions at 100%, adjustable 50–130%. */
export const markScaleOf = (d: LabelDesign) => Math.min(130, Math.max(50, Number(d.pack?.markScale) || 100)) / 100;

/** The carton's short side: the marking table printed on the kraft carton (dark ink, no white cells). */
export function cartonMarkTable(d: LabelDesign, data: LabelData, x: number, y: number, w: number, maxH: number, warnings: string[]): string {
  const sc = markScaleOf(d), val = 8 * sc, cap = 5 * sc, ink = "#2a2016";
  const { rows, L } = markRows(d, data, warnings, false);
  const headerH = val * 3.2;
  const head = tableHeader(x, y, w, headerH, d, data, L, true, val, ink, warnings);
  const table = drawTable(x, y + headerH, w, rows, L, d, cap, val, ink, warnings);
  let barcodeSvg = "", bh = 0;
  if (d.pack?.markBarcode) {
    bh = Math.min(30, val * 3.2);
    const b = barcode(data, x + w / 2, y + headerH + table.h + val * 0.6, w * 0.8, bh, warnings, false);
    barcodeSvg = b.svg + (b.digits ? t(x + w / 2, y + headerH + table.h + val * 0.6 + bh + val * 0.9, b.digits, val * 0.7, { anchor: "middle", fill: "#000" }) : "");
    bh += val * 1.8;
  }
  const total = headerH + table.h + bh;
  if (total > maxH + 0.5) warnings.push(`The carton marking table needs ${Math.ceil(total)} mm but the carton side is ${Math.round(maxH)} mm high: lower the marking size (now ${Math.round(sc * 100)}%).`);
  return head + table.svg + barcodeSvg;
}

/** Carton label sizes (mm). */
export const CARTON_LABEL_SIZES: Array<{ name: string; widthMm: number; heightMm: number }> = [
  { name: "100 × 150", widthMm: 100, heightMm: 150 }, { name: "100 × 100", widthMm: 100, heightMm: 100 }, { name: "150 × 100", widthMm: 150, heightMm: 100 },
];
/** The numbered carton label: white, header (logo + company name), the carton table with CARTON NO "x OF xx" bold, barcode below. */
export function renderCartonLabel(d: LabelDesign, data: LabelData): RenderResult {
  const W = d.widthMm, H = d.heightMm, k = Math.min(W / 100, H / 150, 1.4);
  const warnings: string[] = [];
  const m = 4 * Math.min(W / 100, H / 100);
  const ink = "#111";
  let val = 3.6 * Math.max(0.75, Math.min(W, H) / 100), cap = val * 0.72;
  const { rows, L } = markRows(d, data, warnings, true);
  const draw = () => {
    const w: string[] = [];
    const headerH = val * 3.4;
    const head = tableHeader(m, m, W - 2 * m, headerH, d, data, L, false, val, ink, w);
    const table = drawTable(m, m + headerH, W - 2 * m, rows, L, d, cap, val, ink, w);
    return { head, table, headerH, w };
  };
  let r = draw();
  const bh = Math.max(10, Math.min(18, H * 0.12));
  // Text made smaller step by step until the table and barcode fit (never below 2 mm).
  while (m + r.headerH + r.table.h + bh + val * 2 > H - m && val > 2.05) { val -= 0.1; cap = val * 0.72; r = draw(); }
  warnings.push(...r.w);
  const by = m + r.headerH + r.table.h + val * 0.8;
  const b = barcode(data, W / 2, by, W - 2 * m, bh, warnings, false);
  const digits = b.digits ? t(W / 2, by + bh + val * 0.9, b.digits, val * 0.75, { anchor: "middle" }) : data.sku ? t(W / 2, by + bh + val * 0.9, data.sku, val * 0.75, { anchor: "middle" }) : "";
  if (by + bh + val * 1.2 > H - m + 0.5) warnings.push(`The carton label content doesn't fit ${W} × ${H} mm: choose a larger label.`);
  const body = `<rect x="0.1" y="0.1" width="${(W - 0.2).toFixed(2)}" height="${(H - 0.2).toFixed(2)}" rx="1" fill="#fff" stroke="#9aa4b2" stroke-width="0.25"/>${r.head}${r.table.svg}${b.svg}${digits}`;
  return { svg: svgDoc(W, H, body), warnings: [...new Set(warnings)], scale: k, usedMm: H };
}
